import { PlexUserRelationState, TaskUserRelationState } from '@all-hands-hq/plex-graphql-schema';
import {
  Avatar,
  CardActionArea,
  createStyles,
  FormControl,
  IconButton,
  IconButtonProps,
  makeStyles,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@material-ui/core';
import MuiAddIcon from '@material-ui/icons/Add';
import TaskIcon from '@material-ui/icons/Assignment';
import MuiCheckIcon from '@material-ui/icons/Check';
import MuiCloseIcon from '@material-ui/icons/Close';
import MilestoneIcon from '@material-ui/icons/Flag';
import MuiSearchIcon from '@material-ui/icons/Search';
import React, { SyntheticEvent, useState } from 'react';

import {
  EntityRelationType,
  EntityType,
  PublishStatus,
  TaskDetailsEntityRelationFragment,
  TaskStatus,
  TaskType,
  useCreateTaskEntityRelationMutation,
  useCreateTaskUnderMilestoneMutation,
  useDeleteTaskEntityRelationMutation,
} from '../../../../graphql/gen/types';
import { colorPalette, spacings, TextSizesEnum } from '../../../../theme';
import { notEmpty } from '../../../../utils/notEmpty';
import { ButtonColor } from '../../../shared/primitives/PlexButton';
import { PlexIconButton } from '../../../shared/primitives/PlexIconButton';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { SlateEditorValue } from '../../../shared/slate/SlateEditor';
import { useDialogState } from '../../dialog/useDialogState';
import { canEditTask } from '../../permissions';
import { MoreOptionsMenu } from '../../shared/MoreOptionsMenu';
import { usePlexRoute } from '../usePlexRoute';
import { TaskSearch } from './TaskSearch';
import { TaskStatusView } from './TaskStatus';

type Props = {
  id: string;
  plexId: string;
  publishStatus: PublishStatus;
  dueDate: Date | null;
  permalink: string;
  relatedEntities: TaskDetailsEntityRelationFragment[];
  plexRelation: PlexUserRelationState;
  taskRelation: TaskUserRelationState;
};

type NewEntityState = {
  relationType: EntityRelationType;
  payload:
    | {
        type: 'search';
        entity?: {
          title: string;
          permalink: string;
        };
      }
    | {
        type: 'new';
        title: string;
      };
} | null;

export const EntityRelationTypeView = ({ value }: { value: EntityRelationType }): React.ReactElement => {
  const text = (() => {
    switch (value) {
      case EntityRelationType.CreatedVia:
        return 'Created via';
      case EntityRelationType.DependencyOf:
        return 'Is dependency of';
      case EntityRelationType.DependsOn:
        return 'Depends on';
    }
  })();

  return <span>{text}</span>;
};

function MoreOptionsButton(props: IconButtonProps): React.ReactElement {
  return <IconButton {...props} size={'small'} />;
}

export const TaskRelatedEntities = ({
  id,
  plexId,
  publishStatus,
  dueDate,
  permalink,
  relatedEntities,
  plexRelation,
  taskRelation,
}: Props): React.ReactElement => {
  const classes = useStyles();
  const [, updatePlexRoute] = usePlexRoute();
  const [, setDialogState] = useDialogState();
  const [createTaskEntityRelation, { loading: createRelationLoading }] = useCreateTaskEntityRelationMutation();
  const [createTaskUnderMilestone, { loading: createTaskLoading }] = useCreateTaskUnderMilestoneMutation();
  const [deleteTaskEntityRelation] = useDeleteTaskEntityRelationMutation();
  const [newRelatedEntityState, setNewRelatedEntityState] = useState<NewEntityState>(null);
  const loading = createRelationLoading || createTaskLoading;

  return (
    <div className={classes.root}>
      <div className={classes.headerName}>
        <PlexTypography size={TextSizesEnum.NormalPlus}>Tasks & Dependencies</PlexTypography>
        {canEditTask({ plexRelation, taskRelation }) && newRelatedEntityState == null && (
          <>
            <PlexIconButton
              className={classes.inlineButton}
              onClick={() => {
                setNewRelatedEntityState({
                  relationType: EntityRelationType.DependsOn,
                  payload: { type: 'new', title: '' },
                });
              }}
              MuiIcon={MuiAddIcon}
              color={ButtonColor.Purple}
              tooltip={'Create Task'}
            />
            <PlexIconButton
              className={classes.inlineButton}
              onClick={() => {
                setNewRelatedEntityState({
                  relationType: EntityRelationType.DependsOn,
                  payload: { type: 'search' },
                });
              }}
              MuiIcon={MuiSearchIcon}
              color={ButtonColor.Purple}
              tooltip={'Link Existing Task'}
            />
          </>
        )}
      </div>

      <TableContainer className={classes.tableContainer}>
        <Table size="small" stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell align="left" style={{ width: '200px' }}>
                Relation
              </TableCell>
              <TableCell align="left">Task</TableCell>
              <TableCell style={{ width: '40px' }}>{/* assginee */}</TableCell>
              <TableCell align="right" style={{ width: '50px' }}>
                {/* row actions */}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {newRelatedEntityState?.payload?.type === 'search' && (
              <InlineCreationRow
                relationCellContent={
                  <RelationTypePicker
                    disabled={loading}
                    value={newRelatedEntityState.relationType}
                    onChange={(value) => {
                      setNewRelatedEntityState({
                        ...newRelatedEntityState,
                        relationType: value,
                      });
                    }}
                  />
                }
                itemCellContent={
                  <>
                    {newRelatedEntityState.payload.entity == null && (
                      <TaskSearch
                        ignoreTaskPermalinks={[
                          permalink,
                          ...relatedEntities
                            .map((relatedEntity) => {
                              if (
                                !relatedEntity.deleted &&
                                relatedEntity.target?.__typename === 'Task' &&
                                relatedEntity.type === newRelatedEntityState.relationType
                              ) {
                                return relatedEntity.target.permalink;
                              } else {
                                return null;
                              }
                            })
                            .filter(notEmpty),
                        ]}
                        onSelect={(task) => {
                          setNewRelatedEntityState({
                            ...newRelatedEntityState,
                            payload: {
                              type: 'search',
                              entity: {
                                title: task.body.title || '',
                                permalink: task.body.permalink,
                              },
                            },
                          });
                        }}
                      />
                    )}

                    {newRelatedEntityState.payload.entity != null && (
                      <span className={classes.taskTitle}>{newRelatedEntityState.payload.entity.title}</span>
                    )}
                  </>
                }
                onConfirm={
                  newRelatedEntityState.payload.entity != null && !loading
                    ? () => {
                        if (
                          newRelatedEntityState.payload.type === 'search' &&
                          newRelatedEntityState.payload.entity != null
                        ) {
                          createTaskEntityRelation({
                            variables: {
                              sourceId: id,
                              type: newRelatedEntityState.relationType,
                              targetType: EntityType.Task,
                              targetId: newRelatedEntityState.payload.entity.permalink,
                            },
                          }).then(() => {
                            setNewRelatedEntityState(null);
                          });
                        }
                      }
                    : null
                }
                onCancel={
                  !loading
                    ? () => {
                        setNewRelatedEntityState(null);
                      }
                    : null
                }
              />
            )}

            {newRelatedEntityState?.payload?.type === 'new' && (
              <InlineCreationRow
                relationCellContent={
                  <RelationTypePicker
                    disabled={loading}
                    value={newRelatedEntityState.relationType}
                    onChange={(value) => {
                      setNewRelatedEntityState({
                        ...newRelatedEntityState,
                        relationType: value,
                      });
                    }}
                  />
                }
                itemCellContent={
                  <TextField
                    value={newRelatedEntityState.payload.title}
                    variant="outlined"
                    size="small"
                    disabled={loading}
                    placeholder="Title"
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
                      setNewRelatedEntityState({
                        ...newRelatedEntityState,
                        payload: {
                          type: 'new',
                          title: event.currentTarget.value,
                        },
                      });
                    }}
                    fullWidth
                  />
                }
                onConfirm={
                  newRelatedEntityState.payload.title.length > 0 && !loading
                    ? () => {
                        if (newRelatedEntityState?.payload.type === 'new') {
                          createTaskUnderMilestone({
                            variables: {
                              task: {
                                plexId,
                                type: TaskType.Task,
                                publishStatus,
                                status: TaskStatus.Open,
                                body: SlateEditorValue.new().value,
                                title: newRelatedEntityState.payload.title,
                                dueDate,
                                sourceEntityRelation: {
                                  sourceType: EntityType.Task,
                                  sourceId: id,
                                  type: newRelatedEntityState.relationType,
                                },
                              },
                            },
                          }).then(() => {
                            setNewRelatedEntityState(null);
                          });
                        }
                      }
                    : null
                }
                onCancel={
                  !loading
                    ? () => {
                        setNewRelatedEntityState(null);
                      }
                    : null
                }
              />
            )}

            {relatedEntities
              .filter(
                (relatedEntity) =>
                  !relatedEntity.deleted &&
                  // Source entity handled in `PlexTaskActions`
                  relatedEntity.type !== EntityRelationType.CreatedVia
              )
              .map((relatedEntity) => {
                return (
                  <TableRow key={relatedEntity.id} hover role="checkbox" tabIndex={-1}>
                    <TableCell>
                      <EntityRelationTypeView value={relatedEntity.type} />
                    </TableCell>
                    <TableCell>
                      {relatedEntity.target != null && relatedEntity.target.__typename === 'Task' ? (
                        <CardActionArea
                          className={classes.entityClickableArea}
                          onClick={() => {
                            if (relatedEntity.target != null && relatedEntity.target.__typename === 'Task') {
                              updatePlexRoute({
                                selectedItem: { type: 'task', permalink: relatedEntity.target.permalink },
                              });
                            }
                          }}
                        >
                          <span className={classes.taskType}>
                            {relatedEntity.target.type === TaskType.Task ? (
                              <TaskIcon fontSize="small" />
                            ) : (
                              <MilestoneIcon />
                            )}
                          </span>

                          <span className={classes.taskTitle}>{relatedEntity.target.title}</span>
                          <TaskStatusView className={classes.taskStatus} status={relatedEntity.target.status} />
                        </CardActionArea>
                      ) : relatedEntity.targetType === EntityType.FactReviewProcess ? (
                        <CardActionArea
                          className={classes.entityClickableArea}
                          onClick={() => {
                            setDialogState({ type: 'edit_resource_review_process', id: relatedEntity.targetId });
                          }}
                        >
                          <span className={classes.taskTitle}>Resource Review Milestone Template</span>
                        </CardActionArea>
                      ) : (
                        <div>
                          {relatedEntity.targetType}: {relatedEntity.targetId}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {relatedEntity.target?.__typename === 'Task' &&
                        (relatedEntity.target.assignee != null ? (
                          <Tooltip title={relatedEntity.target.assignee.name}>
                            <Avatar
                              alt={relatedEntity.target.assignee.name}
                              src={relatedEntity.target.assignee.picture}
                              className={classes.assigneeAvatar}
                            />
                          </Tooltip>
                        ) : (
                          ''
                        ))}
                    </TableCell>
                    <TableCell>
                      <div className={classes.editButtons}>
                        {canEditTask({ plexRelation, taskRelation }) && (
                          <MoreOptionsMenu
                            id={relatedEntity.id}
                            className={classes.moreOptionsButton}
                            options={[
                              {
                                onClick: (entityRelationId: string) => {
                                  deleteTaskEntityRelation({ variables: { entityRelationId } });
                                },
                                label: <span className={classes.deleteLabel}>Remove relation</span>,
                              },
                            ]}
                            Button={MoreOptionsButton}
                            menuProps={{
                              anchorOrigin: { horizontal: 'right', vertical: 'top' },
                              transformOrigin: { horizontal: 'right', vertical: 'bottom' },
                            }}
                          />
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

const RelationTypePicker = ({
  disabled,
  value,
  onChange,
}: {
  disabled: boolean;
  value: EntityRelationType;
  onChange: (value: EntityRelationType) => void;
}): React.ReactElement => {
  return (
    <FormControl size="small">
      <Select
        disabled={disabled}
        value={value}
        onChange={(event) => {
          onChange(event.target.value as EntityRelationType);
        }}
        variant="outlined"
      >
        {[EntityRelationType.DependsOn, EntityRelationType.DependencyOf].map((relationType) => (
          <MenuItem key={relationType} value={relationType}>
            <EntityRelationTypeView value={relationType} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const InlineCreationRow = ({
  relationCellContent,
  itemCellContent,
  onConfirm,
  onCancel,
}: {
  relationCellContent: React.ReactNode;
  itemCellContent: React.ReactNode;
  onConfirm: (() => void) | null;
  onCancel: (() => void) | null;
}): React.ReactElement => {
  const classes = useStyles();

  return (
    <TableRow hover role="checkbox" tabIndex={-1}>
      <TableCell>{relationCellContent}</TableCell>
      <TableCell>{itemCellContent}</TableCell>
      <TableCell colSpan={2} align="right">
        <div className={classes.editButtons}>
          <IconButton
            size="small"
            disabled={onConfirm == null}
            className={classes.inlineButton}
            onClick={onConfirm || undefined}
          >
            <MuiCheckIcon />
          </IconButton>
          <IconButton
            size="small"
            disabled={onCancel == null}
            className={classes.inlineButton}
            onClick={onCancel || undefined}
          >
            <MuiCloseIcon />
          </IconButton>
        </div>
      </TableCell>
    </TableRow>
  );
};

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '280px',
    },
    tableContainer: {},
    headerName: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: spacings.normal,
    },
    moreOptionsButton: {},
    taskType: {
      marginRight: spacings.small,
    },
    taskTitle: {
      color: colorPalette.primaryColorBlue._500,
      fontWeight: 600,
      marginRight: spacings.small,
    },
    entityClickableArea: {
      display: 'flex',
      width: 'auto',
    },
    taskStatus: {
      border: `1px solid ${colorPalette.purpleGray._100}`,
      borderRadius: 6,
      padding: spacings.xsmall,
    },
    assigneeAvatar: {
      width: 24,
      height: 24,
    },
    deleteLabel: {
      color: 'red',
    },
    editButtons: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    inlineButton: {
      display: 'inline',
      marginLeft: spacings.small,
    },
  })
);
